import postcss, { type Container, type Rule } from "postcss";
import sanitizeHtmlLibrary from "sanitize-html";
import type sanitizeHtmlTypes from "sanitize-html";

const IMPORT_SCOPE_RE = /^rt-import-[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ALLOWED_AT_RULES = new Set([
  "container",
  "font-face",
  "keyframes",
  "-webkit-keyframes",
  "layer",
  "media",
  "supports",
]);
const URL_ATTRIBUTES = new Set(["href", "src"]);
const ALLOWED_URL_SCHEMES = new Set(["http", "https", "mailto", "tel"]);
const UNSAFE_CSS_VALUE_RE =
  /(?:expression\s*\(|javascript\s*:|vbscript\s*:|@import|<\/?(?:style|script)|\\0|\\0000)/i;
const UNSAFE_CSS_PROPERTY_RE = /^(?:behavior|-moz-binding)$/i;
const FIXED_POSITION_RE = /^(?:fixed|sticky)$/i;
const ORIGINAL_H1_ATTRIBUTE = "data-rt-original-heading";
const SCOPE_ROOT_BOUNDARY_PROPERTIES = new Set([
  "bottom",
  "inset",
  "inset-block",
  "inset-inline",
  "left",
  "position",
  "right",
  "rotate",
  "scale",
  "top",
  "transform",
  "translate",
  "z-index",
]);

function fromCodePointSafe(value: string, radix: number): string {
  const codePoint = Number.parseInt(value, radix);
  if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
    return "";
  }
  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return "";
  }
}

function isSafeUrl(tagName: string, attributeName: string, value: string): boolean {
  const normalized = value
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex: string) => fromCodePointSafe(hex, 16))
    .replace(/&#(\d+);?/g, (_, decimal: string) => fromCodePointSafe(decimal, 10))
    .replace(/[\u0000-\u0020\u007f]/g, "")
    .toLowerCase();
  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(normalized)?.[1];
  if (!scheme) return true;
  if (ALLOWED_URL_SCHEMES.has(scheme)) return true;
  return (
    tagName === "img" &&
    attributeName === "src" &&
    scheme === "data" &&
    normalized.startsWith("data:image/") &&
    !normalized.startsWith("data:image/svg")
  );
}

function cssUrlsAreSafe(
  value: string,
  allowedAssetUrls: ReadonlySet<string>,
): boolean {
  const urlRe = /url\(\s*(["']?)([^)'"\s]+)\1\s*\)/gi;
  let matched = false;
  for (const match of value.matchAll(urlRe)) {
    matched = true;
    const url = match[2]?.trim() ?? "";
    if (!url.startsWith("#") && !allowedAssetUrls.has(url)) return false;
  }
  return !/url\s*\(/i.test(value) || matched;
}

function declarationIsSafe(
  property: string,
  value: string,
  allowedAssetUrls: ReadonlySet<string>,
): boolean {
  if (UNSAFE_CSS_PROPERTY_RE.test(property) || UNSAFE_CSS_VALUE_RE.test(value)) {
    return false;
  }
  if (!cssUrlsAreSafe(value, allowedAssetUrls)) return false;
  if (property === "position" && /\b(?:fixed|sticky)\b/i.test(value)) {
    return false;
  }
  if (property.startsWith("--") && FIXED_POSITION_RE.test(value.trim())) {
    return false;
  }
  if (property === "z-index") {
    const normalized = value.trim().toLowerCase();
    if (normalized !== "auto") {
      const zIndex = Number(normalized);
      if (!Number.isInteger(zIndex) || Math.abs(zIndex) > 100) return false;
    }
  }
  for (const match of value.matchAll(/(-?\d+(?:\.\d+)?)(px|vh|vw)/gi)) {
    const magnitude = Math.abs(Number(match[1]));
    const limit = match[2].toLowerCase() === "px" ? 4_000 : 200;
    if (magnitude > limit) return false;
  }
  return true;
}

function cleanCssContainer(
  container: Container,
  allowedAssetUrls: ReadonlySet<string>,
): void {
  container.walkComments((comment) => {
    comment.remove();
  });
  container.walkAtRules((atRule) => {
    const name = atRule.name.toLowerCase();
    const isKeyframes = name === "keyframes" || name === "-webkit-keyframes";
    if (
      !ALLOWED_AT_RULES.has(name) ||
      !atRule.nodes?.length ||
      (name === "font-face" && atRule.parent?.type !== "root") ||
      (isKeyframes && !/^[a-z_][\w-]*$/i.test(atRule.params.trim())) ||
      /<\/?(?:style|script)/i.test(atRule.params)
    ) {
      atRule.remove();
    }
  });
  container.walkDecls((declaration) => {
    const property = declaration.prop.trim().toLowerCase();
    if (
      !property ||
      !declarationIsSafe(property, declaration.value, allowedAssetUrls)
    ) {
      declaration.remove();
      return;
    }
  });
  container.walkRules((rule) => {
    if (
      !rule.selector.trim() ||
      !rule.nodes?.length ||
      /<\/?(?:style|script)/i.test(rule.selector)
    ) {
      rule.remove();
    }
  });
  container.walkAtRules("font-face", (fontFace) => {
    const declarations = fontFace.nodes ?? [];
    const hasFamily = declarations.some(
      (node) => node.type === "decl" && node.prop.toLowerCase() === "font-family",
    );
    const hasSource = declarations.some(
      (node) => node.type === "decl" && node.prop.toLowerCase() === "src",
    );
    if (!hasFamily || !hasSource) fontFace.remove();
  });
}

/** ChatGPT HTML의 style 블록에서 실행·외부 로드 위험만 제거한다. */
export function sanitizeImportedCss(
  css: string,
  allowedAssetUrls: readonly string[] = [],
): string {
  if (!css.trim()) return "";
  try {
    const root = postcss.parse(css);
    cleanCssContainer(root, new Set(allowedAssetUrls));
    return root.toString().trim();
  } catch {
    return "";
  }
}

function sanitizeInlineStyle(
  style: string,
  allowedAssetUrls: ReadonlySet<string>,
): string {
  try {
    const root = postcss.parse(`x{${style}}`);
    cleanCssContainer(root, allowedAssetUrls);
    const rule = root.first;
    if (!rule || rule.type !== "rule") return "";
    return rule.nodes.map((node) => node.toString()).join(";");
  } catch {
    return "";
  }
}

function sanitizeImportedAttributes(
  tagName: string,
  attributes: sanitizeHtmlTypes.Attributes,
  allowedAssetUrls: ReadonlySet<string>,
): sanitizeHtmlTypes.Tag {
  const next: sanitizeHtmlTypes.Attributes = {};
  for (const [name, value] of Object.entries(attributes)) {
    const attributeName = name.toLowerCase();
    if (attributeName === "style") {
      const style = sanitizeInlineStyle(value, allowedAssetUrls);
      if (style) next[name] = style;
      continue;
    }
    if (attributeName === "srcset") {
      const srcset = sanitizeSrcset(value);
      if (srcset) next[name] = srcset;
      continue;
    }
    if (
      (attributeName === "fill" || attributeName === "stroke") &&
      /url\s*\(/i.test(value) &&
      !/^url\(#[a-z_][\w:.-]*\)$/i.test(value.trim())
    ) {
      continue;
    }
    if (
      (tagName === "use" || tagName === "image") &&
      (attributeName === "href" || attributeName === "xlink:href") &&
      !value.trim().startsWith("#")
    ) {
      continue;
    }
    next[name] =
      URL_ATTRIBUTES.has(attributeName) &&
      !isSafeUrl(tagName, attributeName, value)
        ? "#"
        : value;
  }
  if (tagName === "a" && next.target === "_blank") {
    next.rel = "noopener noreferrer";
  }
  if (tagName === "h1") {
    next[ORIGINAL_H1_ATTRIBUTE] = "h1";
    return { tagName: "h2", attribs: next };
  }
  return { tagName, attribs: next };
}

function sanitizeSrcset(value: string): string {
  const candidates: string[] = [];
  for (const rawCandidate of value.split(",")) {
    const candidate = rawCandidate.trim();
    if (!candidate) continue;
    const [url, ...descriptorParts] = candidate.split(/\s+/);
    if (!url || !isSafeUrl("img", "src", url)) continue;
    const descriptor = descriptorParts.join(" ");
    if (descriptor && !/^(?:\d+(?:\.\d+)?x|\d+w)$/.test(descriptor)) continue;
    candidates.push(descriptor ? `${url} ${descriptor}` : url);
  }
  return candidates.join(", ");
}

const IMPORTED_HTML_OPTIONS: sanitizeHtmlTypes.IOptions = {
  allowedTags: [
    "a", "abbr", "address", "article", "aside", "b", "bdi", "bdo",
    "blockquote", "br", "caption", "cite", "code", "col", "colgroup",
    "dd", "del", "details", "dfn", "div", "dl", "dt", "em",
    "figcaption", "figure", "footer", "h1", "h2", "h3", "h4", "h5", "h6",
    "header", "hr", "i", "img", "kbd", "li", "main", "mark", "nav",
    "ol", "p", "picture", "pre", "q", "s", "samp", "section", "small",
    "source", "svg", "g", "path", "circle", "ellipse", "line", "polyline",
    "polygon", "rect", "defs", "lineargradient", "radialgradient", "stop",
    "use", "clippath", "mask", "pattern", "title", "desc",
    "span", "strong", "sub", "summary", "sup", "table", "tbody", "td",
    "tfoot", "th", "thead", "time", "tr", "u", "ul", "var", "wbr",
  ],
  allowedAttributes: {
    "*": ["aria-*", "class", "data-*", "dir", "id", "lang", "role", "style", "title"],
    a: [
      "href",
      { name: "rel", multiple: true, values: ["noopener", "noreferrer"] },
      { name: "target", values: ["_blank"] },
    ],
    col: ["span", "style"],
    img: ["alt", "height", "loading", "sizes", "src", "srcset", "style", "width"],
    source: ["height", "media", "sizes", "src", "srcset", "type", "width"],
    svg: ["aria-*", "class", "fill", "height", "preserveAspectRatio", "role", "stroke", "style", "viewBox", "viewbox", "width", "xmlns"],
    g: ["class", "fill", "stroke", "stroke-width", "style", "transform"],
    path: ["class", "d", "fill", "fill-rule", "stroke", "stroke-linecap", "stroke-linejoin", "stroke-width", "style", "transform"],
    circle: ["class", "cx", "cy", "fill", "r", "stroke", "stroke-width", "style", "transform"],
    ellipse: ["class", "cx", "cy", "fill", "rx", "ry", "stroke", "stroke-width", "style", "transform"],
    line: ["class", "stroke", "stroke-linecap", "stroke-width", "style", "transform", "x1", "x2", "y1", "y2"],
    polyline: ["class", "fill", "points", "stroke", "stroke-linecap", "stroke-linejoin", "stroke-width", "style", "transform"],
    polygon: ["class", "fill", "points", "stroke", "stroke-width", "style", "transform"],
    rect: ["class", "fill", "height", "rx", "ry", "stroke", "stroke-width", "style", "transform", "width", "x", "y"],
    lineargradient: ["gradientTransform", "gradientUnits", "id", "spreadMethod", "x1", "x2", "y1", "y2"],
    radialgradient: ["cx", "cy", "fx", "fy", "gradientTransform", "gradientUnits", "id", "r", "spreadMethod"],
    stop: ["offset", "stop-color", "stop-opacity", "style"],
    use: ["fill", "height", "href", "stroke", "style", "width", "x", "xlink:href", "y"],
    clippath: ["clipPathUnits", "id", "transform"],
    mask: ["height", "id", "maskContentUnits", "maskUnits", "width", "x", "y"],
    pattern: ["height", "id", "patternContentUnits", "patternTransform", "patternUnits", "preserveAspectRatio", "viewBox", "width", "x", "y"],
    td: ["colspan", "rowspan", "style"],
    th: [
      "colspan",
      "rowspan",
      "style",
      { name: "scope", values: ["col", "row", "colgroup", "rowgroup"] },
    ],
    time: ["datetime"],
  },
  allowedSchemes: ["http", "https", "mailto", "tel"],
  allowedSchemesByTag: { img: ["http", "https", "data"] },
  allowedSchemesAppliedToAttributes: ["href", "src"],
  allowProtocolRelative: false,
  nonTextTags: ["script", "style", "textarea", "option", "iframe", "object", "embed", "foreignobject", "math", "template"],
};

/** class·id·안전한 inline style을 보존하는 원문 디자인 전용 HTML 경계. */
export function sanitizeImportedHtml(
  html: string,
  allowedAssetUrls: readonly string[] = [],
): string {
  if (!html) return html;
  const allowedSet = new Set(allowedAssetUrls);
  return sanitizeHtmlLibrary(html, {
    ...IMPORTED_HTML_OPTIONS,
    transformTags: {
      "*": (tagName, attributes) =>
        sanitizeImportedAttributes(tagName, attributes, allowedSet),
    },
  });
}

/** 구 버전 저장값도 현재의 html/body 가상 루트 계약으로 올린다. */
export function ensureImportedHtmlDocumentRoots(
  html: string,
  allowedAssetUrls: readonly string[] = [],
): string {
  const safeHtml = sanitizeImportedHtml(html, allowedAssetUrls).trim();
  if (!safeHtml) return safeHtml;
  if (
    /^<div\b[^>]*\bdata-rt-import-document(?:=(?:""|'')|(?=[\s>]))[^>]*>\s*<div\b[^>]*\bdata-rt-import-body(?:=(?:""|'')|(?=[\s>]))/i.test(
      safeHtml,
    )
  ) {
    return safeHtml;
  }
  return `<div data-rt-import-document><div data-rt-import-body>${safeHtml}</div></div>`;
}

function normalizeDocumentSelector(selector: string): string {
  const typeBoundary = "(?=$|[\\s>+~,.#:\\[\\)])";
  return selector
    .replace(/html\s*>\s*body/gi, "[data-rt-import-document] > [data-rt-import-body]")
    .replace(/html\s+body/gi, "[data-rt-import-document] [data-rt-import-body]")
    .replace(new RegExp(`(^|[\\s>+~,\\(])html${typeBoundary}`, "gi"), "$1[data-rt-import-document]")
    .replace(new RegExp(`(^|[\\s>+~,\\(])body${typeBoundary}`, "gi"), "$1[data-rt-import-body]")
    .replace(
      new RegExp(`(^|[\\s>+~,\\(])h1${typeBoundary}`, "gi"),
      `$1[${ORIGINAL_H1_ATTRIBUTE}="h1"]`,
    )
    .replace(/:root/g, "[data-rt-import-document]");
}

function selectorTargetsDocumentBoundary(selector: string): boolean {
  return selector.split(",").some((part) => {
    const normalized = part.trim();
    const targetsDocumentRoot =
      normalized.startsWith("[data-rt-import-document]") &&
      !/[\s>+~]/.test(normalized.slice("[data-rt-import-document]".length));
    const targetsBodyRoot =
      /\[data-rt-import-body\](?:[.#:][\w-]+|\[[^\]]+\])*\s*$/.test(
        normalized,
      );
    return targetsDocumentRoot || targetsBodyRoot;
  });
}

function replaceAnimationName(value: string, names: ReadonlyMap<string, string>): string {
  let next = value;
  for (const [name, scopedName] of names) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    next = next.replace(new RegExp(`(^|[^\\w-])${escaped}(?=$|[^\\w-])`, "g"), `$1${scopedName}`);
  }
  return next;
}

function unquoteCssIdentifier(value: string): string | null {
  const normalized = value.trim();
  const match = /^(?:"([^"]+)"|'([^']+)'|([a-z_][\w -]*))$/i.exec(normalized);
  return (match?.[1] ?? match?.[2] ?? match?.[3] ?? "").trim() || null;
}

function replaceFontFamilyName(
  value: string,
  names: ReadonlyMap<string, string>,
): string {
  let next = value;
  for (const [name, scopedName] of names) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    next = next.replace(
      new RegExp(`(["'])${escaped}\\1|(^|[,\\s])${escaped}(?=$|[,\\s/])`, "gi"),
      (matched, quote: string | undefined, prefix: string | undefined) =>
        quote ? `${quote}${scopedName}${quote}` : `${prefix ?? ""}${scopedName}`,
    );
  }
  return next;
}

/** 저장된 안전 CSS가 같은 importedHtml 블록 밖으로 새지 않도록 범위를 고정한다. */
export function scopeImportedCss(
  css: string,
  scope: string,
  allowedAssetUrls: readonly string[] = [],
): string {
  if (!IMPORT_SCOPE_RE.test(scope)) return "";
  const safeCss = sanitizeImportedCss(css, allowedAssetUrls);
  if (!safeCss) return "";
  try {
    const root = postcss.parse(safeCss);
    const keyframeNames = new Map<string, string>();
    const fontFamilyNames = new Map<string, string>();
    const fontFaces = root.nodes.filter(
      (node) => node.type === "atrule" && node.name.toLowerCase() === "font-face",
    );
    for (const fontFace of fontFaces) {
      if (fontFace.type !== "atrule") continue;
      const family = fontFace.nodes?.find(
        (node) => node.type === "decl" && node.prop.toLowerCase() === "font-family",
      );
      if (!family || family.type !== "decl") {
        fontFace.remove();
        continue;
      }
      const originalName = unquoteCssIdentifier(family.value);
      const hasSupportedSource = fontFace.nodes?.some(
        (node) =>
          node.type === "decl" &&
          node.prop.toLowerCase() === "src" &&
          /url\(\s*["']?https:\/\/[^)'"\s]+\.woff2?(?:[?#][^)'"\s]*)?["']?\s*\)/i.test(
            node.value,
          ),
      );
      if (!originalName || !hasSupportedSource) {
        fontFace.remove();
        continue;
      }
      const scopedName = `${scope}-${originalName}`;
      fontFamilyNames.set(originalName, scopedName);
      family.value = family.value.replace(originalName, scopedName);
    }
    root.walkAtRules((atRule) => {
      const name = atRule.name.toLowerCase();
      if (name !== "keyframes" && name !== "-webkit-keyframes") return;
      const originalName = atRule.params.trim();
      const scopedName = `${scope}-${originalName}`;
      keyframeNames.set(originalName, scopedName);
      atRule.params = scopedName;
    });
    root.walkDecls((declaration) => {
      const property = declaration.prop.trim().toLowerCase();
      if (property === "animation" || property === "animation-name") {
        declaration.value = replaceAnimationName(declaration.value, keyframeNames);
      }
      if (property === "font-family" || property === "font") {
        declaration.value = replaceFontFamilyName(
          declaration.value,
          fontFamilyNames,
        );
      }
    });
    root.walkRules((rule: Rule) => {
      const parent = rule.parent;
      if (
        parent?.type === "atrule" &&
        (parent.name.toLowerCase() === "keyframes" ||
          parent.name.toLowerCase() === "-webkit-keyframes")
      ) {
        return;
      }
      rule.selector = normalizeDocumentSelector(rule.selector);
      if (selectorTargetsDocumentBoundary(rule.selector)) {
        rule.walkDecls((declaration) => {
          if (
            SCOPE_ROOT_BOUNDARY_PROPERTIES.has(
              declaration.prop.trim().toLowerCase(),
            )
          ) {
            declaration.remove();
          }
        });
      }
    });
    const scoped = postcss.atRule({
      name: "scope",
      params: `([data-rt-import-scope="${scope}"])`,
    });
    const preservedFontFaces = fontFaces.filter((fontFace) => fontFace.parent);
    for (const fontFace of preservedFontFaces) fontFace.remove();
    scoped.append(root.nodes);
    return [...preservedFontFaces.map((fontFace) => fontFace.toString()), scoped.toString()]
      .filter(Boolean)
      .join("\n");
  } catch {
    return "";
  }
}
